"use client"

import type React from "react"
import { useState, useCallback, useEffect } from "react"
import { X, Upload, FileText, CheckCircle, AlertCircle, Users } from "lucide-react"
import type { DynamicSchema, DynamicUser, DynamicCreateRequest } from "../types/dynamic"
import { SchemaService } from "../services/schema"
import { createDynamicUser } from "../services/api"
import { addToQueue } from "../services/queue"
import { useNetworkStatus } from "../hooks/useNetworkStatus"

interface FieldMapping {
  csvHeader: string
  schemaField: string | null
  confidence: number
}

interface DynamicBulkUploadModalProps {
  schema: DynamicSchema
  existingUsers: DynamicUser[]
  onClose: () => void
  onSuccess: (users: DynamicUser[]) => void
  showToast: (type: "success" | "error" | "info", message: string) => void
  cancelCurrentFetch: () => void
}

const DynamicBulkUploadModal: React.FC<DynamicBulkUploadModalProps> = ({
  schema,
  existingUsers,
  onClose,
  onSuccess,
  showToast,
  cancelCurrentFetch,
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvData, setCsvData] = useState<any[]>([])
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([])
  const [step, setStep] = useState<'upload' | 'mapping' | 'processing'>('upload')
  const [filteredCount, setFilteredCount] = useState<number>(0)
  const [duplicatesInfo, setDuplicatesInfo] = useState<{count: number, fieldName: string} | null>(null)
  const isOnline = useNetworkStatus()

  // Función para limpiar y normalizar texto para comparación
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .replace(/[^a-z0-9]/g, '') // Solo letras y números
  }

  // Función para encontrar la mejor coincidencia entre encabezado CSV y campo del schema
  const findBestMatch = (csvHeader: string): { field: string | null; confidence: number } => {
    const normalizedHeader = normalizeText(csvHeader)
    
    let bestMatch: { field: string | null; confidence: number } = { field: null, confidence: 0 }

    // Buscar en todos los campos del schema
    Object.entries(schema).forEach(([fieldName, fieldConfig]) => {
      // Skip ID field
      if (fieldName === 'id') return

      const normalizedFieldName = normalizeText(fieldName)
      const normalizedLabel = normalizeText(fieldConfig.label)

      // Coincidencia exacta con nombre del campo
      if (normalizedHeader === normalizedFieldName) {
        bestMatch = { field: fieldName, confidence: 100 }
        return
      }

      // Coincidencia exacta con label
      if (normalizedHeader === normalizedLabel) {
        bestMatch = { field: fieldName, confidence: 95 }
        return
      }

      // Coincidencia parcial con nombre del campo
      if (normalizedHeader.includes(normalizedFieldName) || normalizedFieldName.includes(normalizedHeader)) {
        const confidence = Math.max(bestMatch.confidence, 80)
        if (confidence > bestMatch.confidence) {
          bestMatch = { field: fieldName, confidence }
        }
      }

      // Coincidencia parcial con label
      if (normalizedHeader.includes(normalizedLabel) || normalizedLabel.includes(normalizedHeader)) {
        const confidence = Math.max(bestMatch.confidence, 75)
        if (confidence > bestMatch.confidence) {
          bestMatch = { field: fieldName, confidence }
        }
      }

      // Coincidencias específicas comunes
      const commonMappings: { [key: string]: string } = {
        'email': 'email',
        'correo': 'email',
        'mail': 'email',
        'telefono': 'telefono',
        'phone': 'telefono',
        'tel': 'telefono',
        'nombre': 'nombre',
        'name': 'nombre',
        'firstname': 'nombre',
        'apellido': 'apellido',
        'lastname': 'apellido',
        'surname': 'apellido',
        'empresa': 'empresa',
        'company': 'empresa',
        'organizacion': 'empresa',
        'organization': 'empresa',
        'cargo': 'cargo',
        'position': 'cargo',
        'puesto': 'cargo',
        'job': 'cargo',
        'categoria': 'categoria',
        'category': 'categoria',
        'tipo': 'categoria',
        'type': 'categoria',
        'ciudad': 'ciudad',
        'city': 'ciudad',
        'pais': 'pais',
        'country': 'pais',
        'industria': 'industria',
        'industry': 'industria',
        'qr': 'codigo_qr',
        'codigoqr': 'codigo_qr',
        'codigo': 'codigo_qr'
      }

      const mappedField = commonMappings[normalizedHeader]
      if (mappedField && schema[mappedField]) {
        const confidence = 90
        if (confidence > bestMatch.confidence) {
          bestMatch = { field: mappedField, confidence }
        }
      }
    })

    return bestMatch
  }

  // Procesar archivo CSV
  const handleFileUpload = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      showToast("error", "Solo se permiten archivos CSV")
      return
    }

    setCsvFile(file)
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        const lines = text.trim().split(/\r?\n/)
        
        if (lines.length < 2) {
          showToast("error", "El archivo CSV debe tener al menos una línea de encabezados y una línea de datos")
          return
        }

        const headers = lines[0].split(/\t|,/).map(h => h.trim().replace(/['"]/g, ''))
        const rows = lines.slice(1)

        // Parsear datos
        const parsedData = rows.map((line, index) => {
          if (!line.trim()) return null
          
          const cols = line.split(/\t|,/).map(c => c.trim().replace(/['"]/g, ''))
          const rowObj: { [key: string]: string } = {}
          
          headers.forEach((header, i) => {
            rowObj[header] = cols[i] || ''
          })
          
          return { ...rowObj, _rowIndex: index + 2 } // +2 porque empezamos en línea 2
        }).filter(item => item !== null)

        if (parsedData.length === 0) {
          showToast("error", "No se encontraron datos válidos en el archivo CSV")
          return
        }

        setCsvData(parsedData)

        // Crear mapeos automáticos
        const mappings: FieldMapping[] = headers.map(header => {
          const match = findBestMatch(header)
          return {
            csvHeader: header,
            schemaField: match.field,
            confidence: match.confidence
          }
        })

        setFieldMappings(mappings)
        setStep('mapping')
        
        showToast("success", `Archivo procesado: ${parsedData.length} registros encontrados`)
      } catch (error) {
        console.error('Error parsing CSV:', error)
        showToast("error", "Error al procesar el archivo CSV")
      }
    }

    reader.onerror = () => {
      showToast("error", "Error al leer el archivo")
    }

    reader.readAsText(file, 'UTF-8')
  }, [schema, showToast])

  // Calcular duplicados en tiempo real
  const calculateDuplicates = useCallback((mappings: FieldMapping[]) => {
    const fixedField = SchemaService.getFixedField(schema)
    const fixedFieldMapping = mappings.find(m => m.schemaField === fixedField)
    
    if (!fixedField || !fixedFieldMapping) {
      setFilteredCount(csvData.length)
      setDuplicatesInfo(null)
      return
    }

    // Obtener valores existentes del campo fixed
    const existingFixedValues = new Set<string>()
    existingUsers.forEach(user => {
      const value = SchemaService.getFieldValue(user, fixedField)
      if (value !== null && value !== undefined && value !== '') {
        existingFixedValues.add(String(value).toLowerCase().trim())
      }
    })

    // Contar duplicados
    let duplicatesCount = 0
    const filteredData = csvData.filter(row => {
      const csvValue = row[fixedFieldMapping.csvHeader]
      if (!csvValue || csvValue.trim() === '') {
        return true
      }
      
      const normalizedCsvValue = String(csvValue).toLowerCase().trim()
      const isDuplicate = existingFixedValues.has(normalizedCsvValue)
      
      if (isDuplicate) {
        duplicatesCount++
      }
      
      return !isDuplicate
    })

    setFilteredCount(filteredData.length)
    setDuplicatesInfo(duplicatesCount > 0 ? {
      count: duplicatesCount,
      fieldName: schema[fixedField]?.label || fixedField
    } : null)
  }, [csvData, existingUsers, schema])

  // Efecto para calcular duplicados automáticamente cuando se cargan los datos
  useEffect(() => {
    if (csvData.length > 0 && fieldMappings.length > 0) {
      calculateDuplicates(fieldMappings)
    }
  }, [csvData, fieldMappings, calculateDuplicates])

  // Actualizar mapeo de campo
  const updateFieldMapping = (csvHeader: string, schemaField: string | null) => {
    const newMappings = fieldMappings.map(mapping => 
      mapping.csvHeader === csvHeader 
        ? { ...mapping, schemaField, confidence: schemaField ? 100 : 0 }
        : mapping
    )
    setFieldMappings(newMappings)
    
    // Recalcular duplicados con los nuevos mapeos
    calculateDuplicates(newMappings)
  }

  // Procesar y crear usuarios
  const handleCreateUsers = async () => {
    if (csvData.length === 0) return

    setStep('processing')
    
    try {
      // Validar que al menos haya algunos mapeos
      const validMappings = fieldMappings.filter(m => m.schemaField !== null)
      if (validMappings.length === 0) {
        showToast("error", "Debe mapear al menos un campo")
        setStep('mapping')
        return
      }

      // Obtener el campo fixed del schema
      const fixedField = SchemaService.getFixedField(schema)
      
      // Verificar si hay mapeo del campo fixed
      const fixedFieldMapping = fieldMappings.find(m => m.schemaField === fixedField)
      
      // Filtrar duplicados basándose en el campo fixed si existe
      let filteredCsvData = csvData
      let duplicatesCount = 0
      
      if (fixedField && fixedFieldMapping) {
        console.log(`🔍 Verificando duplicados usando campo fixed: ${fixedField}`)
        
        // Obtener valores existentes del campo fixed de usuarios locales
        const existingFixedValues = new Set<string>()
        existingUsers.forEach(user => {
          const value = SchemaService.getFieldValue(user, fixedField)
          if (value !== null && value !== undefined && value !== '') {
            existingFixedValues.add(String(value).toLowerCase().trim())
          }
        })
        
        console.log(`📊 Valores existentes encontrados: ${existingFixedValues.size}`)
        
        // Filtrar filas del CSV que no tengan duplicados
        const originalCount = csvData.length
        filteredCsvData = csvData.filter(row => {
          const csvValue = row[fixedFieldMapping.csvHeader]
          if (!csvValue || csvValue.trim() === '') {
            return true // Mantener filas sin valor en campo fixed
          }
          
          const normalizedCsvValue = String(csvValue).toLowerCase().trim()
          const isDuplicate = existingFixedValues.has(normalizedCsvValue)
          
          if (isDuplicate) {
            console.log(`🚫 Fila duplicada omitida: ${fixedField} = ${csvValue}`)
            duplicatesCount++
          }
          
          return !isDuplicate
        })
        
        console.log(`✂️ Filtrado completado: ${originalCount} → ${filteredCsvData.length} (${duplicatesCount} duplicados omitidos)`)
        
        // Informar al usuario sobre duplicados encontrados
        if (duplicatesCount > 0) {
          showToast("info", `Se omitieron ${duplicatesCount} registros duplicados basados en ${schema[fixedField]?.label || fixedField}`)
        }
        
        // Si todas las filas fueron filtradas por duplicados
        if (filteredCsvData.length === 0) {
          showToast("error", "Todos los registros del CSV ya existen en el sistema")
          setStep('mapping')
          return
        }
      }

      // Construir usuarios usando los datos filtrados
      const users: DynamicCreateRequest[] = filteredCsvData.map((row) => {
        const user: DynamicCreateRequest = {
          nombre: '',
          apellido: '',
          email: '',
          telefono: '',
          extra: {}
        }

        // Aplicar mapeos
        fieldMappings.forEach(mapping => {
          if (mapping.schemaField && row[mapping.csvHeader] !== undefined) {
            const value = row[mapping.csvHeader]
            const fieldConfig = schema[mapping.schemaField]
            
            // Procesar valor según tipo de campo
            let processedValue: any = value
            
            if (fieldConfig) {
              switch (fieldConfig.type) {
                case 'number':
                  processedValue = value === '' ? 0 : Number(value) || 0
                  break
                case 'boolean':
                  processedValue = ['true', '1', 'sí', 'si', 'yes', 'verdadero'].includes(value.toLowerCase())
                  break
                case 'string':
                case 'enum':
                default:
                  // Normalización específica por campo
                  if (mapping.schemaField === 'email') {
                    processedValue = value.toLowerCase().trim()
                  } else if (mapping.schemaField === 'telefono') {
                    processedValue = value.replace(/[^\d\s()+-]/g, "")
                  } else if (mapping.schemaField === 'nombre' || mapping.schemaField === 'apellido') {
                    processedValue = value.replace(/\s+/g, " ").trim()
                  } else {
                    processedValue = value.trim()
                  }
                  break
              }
            }

            // Asignar a campo principal o extra
            const mainFields = ['nombre', 'apellido', 'email', 'telefono']
            if (mainFields.includes(mapping.schemaField)) {
              (user as any)[mapping.schemaField] = processedValue
            } else {
              user.extra[mapping.schemaField] = processedValue
            }
          }
        })

        return user
      })

      // Cancelar fetch actual
      cancelCurrentFetch()

      // Crear usuarios usando el sistema existente
      const createUsersPayload = users

      const createUsers = async () => {
        if (isOnline) {
          try {
            const result = await createDynamicUser(createUsersPayload)
            
            // Manejar respuesta
            if (Array.isArray(result)) {
              // Respuesta exitosa con array de usuarios creados
              showToast("success", `${result.length} usuarios creados exitosamente`)
              onSuccess(result)
              onClose()
            } else {
              // Manejo genérico de respuesta exitosa
              showToast("success", "Usuarios creados exitosamente")
              onClose()
            }
          } catch (networkError: any) {
            console.warn("Error en creación masiva:", networkError)
            
            // Verificar si es un error 207 (algunos usuarios no se pudieron crear)
            if (networkError.message && networkError.message.includes("207")) {
              // Error 207: Algunos usuarios no se pudieron crear (probablemente duplicados)
              // NO agregar a la cola porque volverá a fallar
              showToast("error", "Algunos usuarios no se pudieron crear (posibles duplicados). Revisa los datos e intenta nuevamente.")
              setStep('mapping') // Volver al mapeo para que el usuario pueda revisar
              return
            }
            
            // Para otros errores de red, agregar a la cola
            await addToQueue({
              type: "create_dynamic_user_bulk",
              data: createUsersPayload as any,
              timestamp: Date.now(),
            })
            showToast("info", "Usuarios guardados localmente, se sincronizarán cuando haya conexión")
            onClose()
          }
        } else {
          await addToQueue({
            type: "create_dynamic_user_bulk",
            data: createUsersPayload as any,
            timestamp: Date.now(),
          })
          showToast("info", "Usuarios guardados localmente, se sincronizarán cuando haya conexión")
          onClose()
        }
      }

      await createUsers()
      
    } catch (error) {
      console.error('Error creating users:', error)
      showToast("error", "Error al crear los usuarios")
      setStep('mapping')
    } finally {
    }
  }

  // Obtener campos disponibles del schema (excluyendo ID)
  const availableFields = Object.entries(schema)
    .filter(([fieldName]) => fieldName !== 'id')
    .map(([fieldName, fieldConfig]) => ({
      value: fieldName,
      label: fieldConfig.label
    }))

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white/40 backdrop-blur-sm px-6 pt-6 pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Carga Masiva de Usuarios
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Step Upload */}
            {step === 'upload' && (
              <div className="space-y-6">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <h4 className="mt-2 text-lg font-medium text-gray-900">Subir archivo CSV</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Selecciona un archivo CSV con los datos de los usuarios
                  </p>
                </div>

                {/* File upload area */}
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-gray-400 transition-colors"
                  onDrop={(e) => {
                    e.preventDefault()
                    const file = e.dataTransfer.files[0]
                    if (file) handleFileUpload(file)
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <FileText className="mx-auto h-8 w-8 text-gray-400 mb-4" />
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Arrastra tu archivo CSV aquí o
                    </p>
                    <label className="inline-flex items-center px-4 py-2 bg-blue-600/20 backdrop-blur-sm border border-blue-400/30 text-blue-800 text-sm font-medium rounded-xl hover:bg-blue-600/30 hover:border-blue-500/40 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 cursor-pointer">
                      <Upload className="h-4 w-4 mr-2" />
                      Seleccionar archivo
                      <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) handleFileUpload(file)
                        }}
                        className="sr-only"
                      />
                    </label>
                  </div>
                </div>

                {/* Info */}
                <div className="bg-blue-50 p-4 rounded-xl">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Requisitos del archivo CSV
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <ul className="list-disc pl-5 space-y-1">
                          <li>Primera fila debe contener los encabezados</li>
                          <li>Separado por comas (,) o tabulaciones</li>
                          <li>Codificación UTF-8 recomendada</li>
                          <li>Los encabezados se mapearán automáticamente con los campos disponibles</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step Mapping */}
            {step === 'mapping' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Mapeo de Campos</h4>
                    <p className="text-sm text-gray-500">
                      Archivo: {csvFile?.name} - {csvData.length} registros
                    </p>
                  </div>
                  <button
                    onClick={() => setStep('upload')}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Cambiar archivo
                  </button>
                </div>

                <div className="bg-amber-50 p-4 rounded-xl">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-amber-700">
                        Revisa y corrige el mapeo automático de campos. Los campos sin mapear se ignorarán.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Información de duplicados */}
                {duplicatesInfo && (
                  <div className={`p-4 rounded-xl ${filteredCount === 0 ? 'bg-red-50' : 'bg-orange-50'}`}>
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertCircle className={`h-5 w-5 ${filteredCount === 0 ? 'text-red-400' : 'text-orange-400'}`} />
                      </div>
                      <div className="ml-3">
                        <h3 className={`text-sm font-medium ${filteredCount === 0 ? 'text-red-800' : 'text-orange-800'}`}>
                          {filteredCount === 0 ? 'Todos los registros son duplicados' : 'Duplicados detectados'}
                        </h3>
                        <p className={`text-sm mt-1 ${filteredCount === 0 ? 'text-red-700' : 'text-orange-700'}`}>
                          {filteredCount === 0 ? (
                            <>
                              <strong>Todos los {duplicatesInfo.count} registros del CSV ya existen</strong> en el sistema basados en el campo <strong>{duplicatesInfo.fieldName}</strong>.
                              No se puede continuar con la carga masiva.
                            </>
                          ) : (
                            <>
                              Se encontraron <strong>{duplicatesInfo.count} registros duplicados</strong> basados en el campo <strong>{duplicatesInfo.fieldName}</strong>.
                              Solo se crearán <strong>{filteredCount} usuarios nuevos</strong>, omitiendo automáticamente los duplicados.
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Información adicional cuando no hay duplicados pero sí campo fixed */}
                {!duplicatesInfo && filteredCount === csvData.length && SchemaService.getFixedField(schema) && fieldMappings.find(m => m.schemaField === SchemaService.getFixedField(schema)) && (
                  <div className="bg-green-50 p-4 rounded-xl">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          Sin duplicados detectados
                        </h3>
                        <p className="text-sm text-green-700 mt-1">
                          Todos los <strong>{csvData.length} registros</strong> son únicos basados en el campo <strong>{schema[SchemaService.getFixedField(schema)!]?.label || SchemaService.getFixedField(schema)}</strong>.
                          Se crearán todos los usuarios.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mappings */}
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {fieldMappings.map((mapping, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border border-gray-200 rounded-xl">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Encabezado CSV
                        </label>
                        <div className="p-2 bg-gray-50 rounded-lg text-sm">
                          {mapping.csvHeader}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Campo del Sistema
                        </label>
                        <select
                          value={mapping.schemaField || ''}
                          onChange={(e) => updateFieldMapping(mapping.csvHeader, e.target.value || null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Sin mapear</option>
                          {availableFields.map(field => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confianza
                        </label>
                        <div className="flex items-center space-x-2">
                          <div className={`h-2 w-full rounded-full ${
                            mapping.confidence >= 80 ? 'bg-green-200' : 
                            mapping.confidence >= 60 ? 'bg-yellow-200' : 'bg-red-200'
                          }`}>
                            <div 
                              className={`h-2 rounded-full ${
                                mapping.confidence >= 80 ? 'bg-green-500' : 
                                mapping.confidence >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${mapping.confidence}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-500">{mapping.confidence}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-500/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-500/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreateUsers}
                    disabled={fieldMappings.filter(m => m.schemaField !== null).length === 0 || filteredCount === 0}
                    className="inline-flex items-center px-4 py-2 bg-green-600/20 backdrop-blur-sm border border-green-400/30 text-green-800 text-sm font-medium rounded-xl hover:bg-green-600/30 hover:border-green-500/40 hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Crear {filteredCount > 0 ? filteredCount : csvData.length} Usuarios
                    {duplicatesInfo && (
                      <span className="ml-1 text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded-full">
                        {duplicatesInfo.count} omitidos
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step Processing */}
            {step === 'processing' && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                  </div>
                  <h4 className="mt-4 text-lg font-medium text-gray-900">Creando usuarios...</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Procesando {csvData.length} registros. Esto puede tomar unos momentos.
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-xl">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        {isOnline 
                          ? "Creando usuarios en línea..." 
                          : "Sin conexión: los usuarios se guardarán localmente y se sincronizarán cuando haya conexión"
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DynamicBulkUploadModal
